import React from "react"
import { Block, Text } from "@convertkit/slate"
import { useValue } from "../editor-context"

export function createCaptioned({ type, caption, data }) {
  const block =
    typeof caption === "string"
      ? Block.create({ type: "caption", nodes: [Text.create(caption)] })
      : caption

  // We have to pass `data` into Object.create due to the `width`, `alt` and `borderRadius`
  // attributes, but `src` gets added in src/plugins/images.js and we don't need it here.
  const { src, ...styles } = data || {}

  const captioned = Block.create({
    object: "block",
    type: "captioned-image",
    nodes: [
      Block.create({
        object: "block",
        type,
        data
      }),
      block
    ],
    data: styles
  })

  return captioned
}

function Caption({ editor: _editor, node, children, attributes }) {
  // NOTE: Trying to use the value from `editor` does not work. Without hooking
  // into `useValue`, this component won't update unless it changes.
  const value = useValue()

  // The caption should only be visible if:
  //   a) it is not blank or
  //   b) the captioned element has focus or
  //   c) the caption itself has focus
  const parent = value.document.getParent(node.key)
  const focusBlock = value.focusBlock
  const focusParent = focusBlock && value.document.getParent(focusBlock.key)

  const visible =
    (value.selection.isFocused && focusParent == parent) ||
    node.getText() !== ""

  return (
    <figcaption
      style={{ textAlign: "center", display: visible ? "block" : "none" }}
      {...attributes}
    >
      {children}
    </figcaption>
  )
}

export default ({ type, defaultBlock: _defaultBlock = "paragraph" }) => {
  return {
    schema: {
      blocks: {
        [`captioned-${type}`]: {
          nodes: [
            {
              match: { type },
              min: 1,
              max: 1
            },
            {
              match: { type: "caption" },
              min: 1,
              max: 1
            }
          ],
          normalize: (editor, error) => {
            switch (error.code) {
              case "child_min_invalid":
                // caption is missing; insert a caption
                if (error.index === 1) {
                  editor.insertNodeByKey(error.node.key, 1, {
                    object: "block",
                    type: "caption"
                  })
                } else {
                  // `type` is missing; remove captioned-`type`
                  editor.removeNodeByKey(error.node.key)
                }
                return
              default:
                return
            }
          }
        },
        caption: {
          nodes: [
            {
              match: [{ object: "inline" }, { object: "text" }]
            }
          ]
        }
      }
    },
    commands: {
      insertCaptioned: (editor, { type, caption, data }) => {
        const block = createCaptioned({ type, caption, data })

        editor.insertBlock(block)
      }
    },
    renderNode(props, editor, next) {
      const { node, attributes, children } = props

      switch (node.type) {
        case `captioned-${type}`:
          return <figure {...attributes}>{children}</figure>
        case "caption":
          return (
            <Caption editor={editor} node={node} attributes={attributes}>
              {children}
            </Caption>
          )
        default:
          return next()
      }
    },

    onKeyDown(event, editor, next) {
      if (event.key !== "Enter" || event.shiftKey) return next()
      if (editor.value.startBlock.type !== "caption") return next()

      const caption = editor.value.startBlock
      const document = editor.value.document
      const captioned = document.getParent(caption.key)
      const parent = document.getParent(captioned.key)

      const index = parent.nodes.indexOf(captioned)

      editor.insertNodeByKey(parent.key, index + 1, {
        object: "block",
        type: "paragraph"
      })
      editor.moveToEndOfBlock(captioned).moveForward(1)
    }
  }
}
